import React, { useCallback, useEffect, useState } from 'react';
import {
  getCourseTariffs,
  createCourseTariff,
  updateCourseTariff,
  retireCourseTariff,
} from '../../api/tariffs';
import errorText from '../../utils/errorText';
import { formatMoney } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import { useConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import './CourseTariffs.css';

// A row's `key` is its id once saved; a row that was only added carries a temporary one, so a
// reload can tell which unsaved rows to keep.
let newRowSeq = 0;

const toRow = (tier) => ({
  key: tier.id,
  id: tier.id,
  name: tier.name || '',
  price: String(Number(tier.price) || 0),
  description: tier.description || '',
  isActive: tier.isActive !== false,
  isDefault: Boolean(tier.isDefault),
  orderIndex: tier.orderIndex ?? 0,
  dirty: false,
});

/**
 * Course tariffs, edited from the CRM: name, price, description, active, default. Each tariff
 * saves on its own. The backend keeps the course price equal to the cheapest active tariff, so
 * the parent refreshes the course after every save (onCourseChange).
 */
export default function CourseTariffs({ courseId, currency = 'UZS', onCourseChange, onTariffsLoaded }) {
  const { t } = useI18n();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  // `keepEdits` is the refresh after one row was saved or retired (`doneKey`): it must not throw away
  // what was typed into the other rows, so rows with unsaved changes (new ones included) stay as
  // they are and only the rest is replaced by the server's version.
  const load = useCallback(async ({ keepEdits = false, doneKey = null } = {}) => {
    if (!keepEdits) setLoading(true);
    setError(null);
    try {
      const list = await getCourseTariffs(courseId);
      setRows((current) => {
        if (!keepEdits) return list.map(toRow);
        const mine = new Map(current.filter((r) => r.dirty && r.key !== doneKey).map((r) => [r.key, r]));
        // An unsaved "default" choice wins over the default the server still has.
        const pendingDefault = [...mine.values()].some((r) => r.isDefault);
        const merged = list.map((tier) => {
          const row = toRow(tier);
          return mine.get(tier.id) || (pendingDefault ? { ...row, isDefault: false } : row);
        });
        return [...merged, ...current.filter((r) => !r.id && mine.has(r.key))];
      });
      onTariffsLoaded?.(list);
    } catch (e) {
      setError(errorText(e, t('courseOverview.tariffsLoadError')));
    } finally {
      setLoading(false);
    }
  }, [courseId, onTariffsLoaded, t]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (index, change) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...change, dirty: true } : row)));

  const addRow = () => {
    const key = `new-${++newRowSeq}`;
    setRows((current) => [
      ...current,
      {
        key,
        name: '',
        price: '',
        description: '',
        isActive: true,
        isDefault: current.length === 0,
        orderIndex: current.length + 1,
        dirty: true,
      },
    ]);
  };

  const save = async (index) => {
    const row = rows[index];
    const price = Number(row.price);
    if (row.name.trim().length < 2) {
      setError(t('courseOverview.tariffNameError'));
      return;
    }
    if (row.price === '' || !Number.isFinite(price) || price < 0) {
      setError(t('courseOverview.tariffPriceError'));
      return;
    }
    const body = {
      name: row.name.trim(),
      price,
      description: row.description.trim() || null,
      isActive: row.isActive,
      isDefault: row.isDefault,
      orderIndex: row.orderIndex,
    };
    setBusy(index);
    setError(null);
    try {
      if (row.id) await updateCourseTariff(courseId, row.id, body);
      else await createCourseTariff(courseId, body);
      setNotice(t('courseOverview.tariffSaved', { name: body.name }));
      setTimeout(() => setNotice(null), 2400);
      await load({ keepEdits: true, doneKey: row.key });
      onCourseChange?.();
    } catch (e) {
      setError(errorText(e, t('courseOverview.tariffSaveError')));
    } finally {
      setBusy(null);
    }
  };

  const retire = async (index) => {
    const row = rows[index];
    if (!row.id) {
      setRows((current) => current.filter((_, i) => i !== index));
      return;
    }
    const sure = await confirm({
      title: t('courseOverview.tariffRetireConfirm', { name: row.name }),
      confirmLabel: t('courseOverview.tariffRetire'),
      tone: 'danger',
    });
    if (!sure) return;
    setBusy(index);
    setError(null);
    try {
      await retireCourseTariff(courseId, row.id);
      await load({ keepEdits: true, doneKey: row.key });
      onCourseChange?.();
    } catch (e) {
      setError(errorText(e, t('courseOverview.tariffSaveError')));
    } finally {
      setBusy(null);
    }
  };

  const active = rows.filter((r) => r.id && r.isActive);
  const cheapest = active.length ? Math.min(...active.map((r) => Number(r.price) || 0)) : null;

  return (
    <div className="tariffs">
      <div className="tariffs-top">
        <p className="settings-help">
          {cheapest === null
            ? t('courseOverview.tariffsHelpEmpty')
            : t('courseOverview.tariffsHelp', { price: formatMoney(cheapest, currency, { free: false }) })}
        </p>
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={addRow} disabled={loading}>
          {t('courseOverview.tariffAdd')}
        </button>
      </div>

      {error && <div className="settings-banner is-error" role="alert">{error}</div>}
      {notice && <div className="settings-banner is-ok" role="status">{notice}</div>}

      {loading ? (
        <div className="tariffs-skeleton" aria-busy="true" />
      ) : rows.length === 0 ? null : (
        <ul className="tariffs-list">
          {rows.map((row, index) => (
            <li key={row.key} className={`tariff-row ${row.isActive ? '' : 'is-inactive'}`}>
              <div className="tariff-fields">
                <label className="settings-field">
                  <span className="settings-label">{t('courseOverview.tariffName')}</span>
                  <input
                    className="tariff-input"
                    type="text"
                    maxLength={80}
                    value={row.name}
                    placeholder={t('courseOverview.tariffNamePlaceholder')}
                    onChange={(e) => patch(index, { name: e.target.value })}
                  />
                </label>
                <label className="settings-field">
                  <span className="settings-label">{t('courseOverview.tariffPrice')}</span>
                  <div className="settings-price-input">
                    <input
                      type="number"
                      min={0}
                      step={1000}
                      value={row.price}
                      onChange={(e) => patch(index, { price: e.target.value })}
                    />
                    <span className="settings-price-suffix">{currency}</span>
                  </div>
                </label>
                <label className="settings-field settings-field-full">
                  <span className="settings-label">{t('courseOverview.tariffDescription')}</span>
                  <textarea
                    className="tariff-input"
                    rows={2}
                    maxLength={2000}
                    value={row.description}
                    onChange={(e) => patch(index, { description: e.target.value })}
                  />
                </label>
              </div>
              <div className="tariff-side">
                <label className="tariff-check">
                  <input
                    type="checkbox"
                    checked={row.isActive}
                    onChange={(e) => patch(index, { isActive: e.target.checked })}
                  />
                  {t('courseOverview.tariffActive')}
                </label>
                <label className="tariff-check">
                  <input
                    type="radio"
                    name={`default-tariff-${courseId}`}
                    checked={row.isDefault}
                    onChange={() =>
                      setRows((current) =>
                        current.map((r, i) => ({ ...r, isDefault: i === index, dirty: r.dirty || i === index }))
                      )
                    }
                  />
                  {t('courseOverview.tariffDefault')}
                </label>
                <div className="tariff-actions">
                  <button
                    type="button"
                    className="ds-btn ds-btn-primary ds-btn-sm"
                    disabled={!row.dirty || busy !== null}
                    onClick={() => save(index)}
                  >
                    {busy === index ? t('courseOverview.saving') : t('courseOverview.tariffSave')}
                  </button>
                  <button
                    type="button"
                    className="ds-btn ds-btn-secondary ds-btn-sm"
                    disabled={busy !== null}
                    onClick={() => retire(index)}
                  >
                    {row.id ? t('courseOverview.tariffRetire') : t('courseOverview.discard')}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {confirmDialog}
    </div>
  );
}
